/**
 * Photo capture helpers (specs/changes/photo-capture Phase 3).
 * Client-side downscale before upload (longest edge 2048 px, JPEG q85 — keeps
 * whiteboard text legible while staying under the 5 MB analysis limit) and
 * the markdown section a captured photo contributes to the created document.
 * Pure logic (dimensions, section building, type checks) is exported for tests;
 * the canvas work stays thin.
 */

export const MAX_EDGE_PX = 2048;
export const JPEG_QUALITY = 0.85;
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

const CAPTURE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

/** Whether a picked file is an image the media API accepts. */
export function isSupportedImage(file: { type: string; size: number }): string | null {
  if (!CAPTURE_TYPES.has(file.type)) {
    return 'Unsupported image type — use JPEG, PNG, or WebP.';
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return 'Image is larger than 10 MB — retake or pick a smaller photo.';
  }
  return null;
}

/** Target dimensions with the longest edge capped, aspect preserved (pure). */
export function fitWithin(width: number, height: number, maxEdge: number = MAX_EDGE_PX): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= maxEdge) return { width, height };
  const scale = maxEdge / longest;
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

/**
 * Downscale an image file for analysis. Re-encodes as JPEG q85 when scaling
 * is needed; returns the original file untouched when it already fits.
 */
export async function downscaleImage(
  file: File,
): Promise<{ blob: Blob; contentType: string }> {
  const bitmap = await createImageBitmap(file);
  try {
    const { width, height } = fitWithin(bitmap.width, bitmap.height);
    if (width === bitmap.width && height === bitmap.height) {
      return { blob: file, contentType: file.type };
    }
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return { blob: file, contentType: file.type };
    ctx.drawImage(bitmap, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
    );
    return blob
      ? { blob, contentType: 'image/jpeg' }
      : { blob: file, contentType: file.type };
  } finally {
    bitmap.close();
  }
}

export interface CapturedPhoto {
  mediaId: string;
  mermaid: string | null;
}

/**
 * The markdown section captured photos contribute to a created document:
 * a stable media:// reference per photo (rendering lands in Phase 4) with
 * its mermaid rendition beneath, per the spec.
 */
export function buildMediaSection(photos: CapturedPhoto[]): string {
  if (photos.length === 0) return '';
  const parts = photos.map((p) => {
    const image = `![Captured photo](media://${p.mediaId})`;
    return p.mermaid ? `${image}\n\n\`\`\`mermaid\n${p.mermaid.trim()}\n\`\`\`` : image;
  });
  return `\n\n## Captured media\n\n${parts.join('\n\n')}\n`;
}
